import type { PublicSigninAnnouncement } from 'types';
import type { SigninReply } from 'definitions';
import type { ChatChapter, ChatMessage, ChatQuestion } from './chat-types';

/**
 * Evergreen copy, played when nothing is published. The panel is half the login
 * screen, so an empty chat reads as a broken page rather than as an absence of
 * news. It carries no date because it is not news.
 */
export const EVERGREEN_CHAPTER: ChatChapter = {
  messages: [
    { kind: 'text', from: 'pt', text: 'Hey, welcome back.' },
    { kind: 'headline', text: 'Web experiments that move the needle.' },
    {
      kind: 'text',
      from: 'pt',
      text: "The world's lightest A/B testing platform: 0.9 KB, cookie-free, and running on more than 12% of Sweden's internet traffic.",
    },
  ],
  questions: [],
};

// Spelled out rather than left to Intl: en-GB abbreviates September to "Sept" and
// the rest to three letters, which makes one date in twelve look misprinted beside
// its neighbours.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

export const formatAnnouncementDate = (timestamp: number): string => {
  const date = new Date(timestamp);
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

const answerFor = (reply: SigninReply, announcement: string): ChatMessage[] => {
  if (reply.kind === 'link') return [{ kind: 'link', label: reply.label, url: reply.url, announcement }];
  return [{ kind: 'text', from: 'pt', text: reply.text }];
};

const questionFor = (reply: SigninReply, announcement: string): ChatQuestion => ({
  question: reply.question,
  answer: answerFor(reply, announcement),
});

/**
 * One chapter per announcement, newest first, which is the order the public route
 * serves them in. Blank parts are skipped rather than rendered as empty bubbles:
 * a draft with no intro is a valid announcement, a blank bubble is not a valid
 * message.
 */
const chapterFor = (announcement: PublicSigninAnnouncement): ChatChapter => {
  const messages: ChatMessage[] = [{ kind: 'date', label: formatAnnouncementDate(announcement.publishedAt) }];

  if (announcement.intro) messages.push({ kind: 'text', from: 'pt', text: announcement.intro });
  messages.push({ kind: 'headline', text: announcement.headline });
  if (announcement.body) messages.push({ kind: 'text', from: 'pt', text: announcement.body });

  const key = String(announcement.id);
  return { messages, questions: announcement.replies.map((reply) => questionFor(reply, key)) };
};

export const buildNewsChapters = (announcements: readonly PublicSigninAnnouncement[]): ChatChapter[] => {
  if (announcements.length === 0) return [EVERGREEN_CHAPTER];
  return announcements.map(chapterFor);
};
